import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { getAppProfile, updateProfile } from "../../../../store/actions/AppProfileAction";
import Swal from "sweetalert2"; // Import SweetAlert2
import Loader from "../../Common/Loader.jsx";

const AppProfile = ({
  profile,
  loading,
  updating,
  getAppProfile,
  updateProfile,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    username: "",
    role_id: "",
  });
  const [validationErrors, setValidationErrors] = useState({});

  // Fetch profile data on mount
  useEffect(() => {
    getAppProfile();
  }, [getAppProfile]);

  // Set form data when profile is loaded
  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || "",
        email: profile.email || "",
        mobile: profile.mobile || "",
        username: profile.username || "",
        role_id: profile.role_id || "",
      });
    }
  }, [profile]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // For mobile number, make sure only digits are allowed
    if (name === "mobile") {
      const numericValue = value.replace(/\D/g, ""); // Remove any non-digit characters
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(phone); // Only 10 digits allowed
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    if (!formData.name) {
      errors.name = "Name is required";
    }

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    if (!formData.mobile) {
      errors.mobile = "Mobile number is required";
    } else if (!validatePhone(formData.mobile)) {
      errors.mobile = "Mobile number must be exactly 10 digits";
    }

    if (!formData.username) {
      errors.username = "Username is required";
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form data
    const errors = validateForm();
    setValidationErrors(errors);

    // If there are validation errors, stop the form submission
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      // Call the updateProfile action
      await updateProfile(formData);
      
      // Show success message with SweetAlert2 if profile update is successful
      Swal.fire({
        icon: "success",
        title: "Profile Updated",
        text: "Your profile has been successfully updated!",
      });

      // Refetch the updated profile data
      setReloading(true);
      await getAppProfile();
    } catch (err) {
      // Show error message with SweetAlert2 if an error occurs
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Something went wrong while updating your profile.",
      });
    } finally {
      setReloading(false);
    }
  };

  // Handle edit mode toggle
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (isEditing && profile) {
      // Reset to original values if canceling edit
      setFormData({
        name: profile.name || "",
        email: profile.email || "",
        mobile: profile.mobile || "",
        username: profile.username || "",
        role_id: profile.role_id || "",
      });
    }
  };

  // Show loader if the profile is being loaded or reloaded
  if (loading || reloading) {
    return <Loader />;
  }

  return (
    <div className="row justify-content-center">
      <div className="col-xl-8">
        <div className="card shadow">
          <div className="card-body">
            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  name="name"
                  className={`form-control ${validationErrors.name ? "is-invalid" : ""}`}
                  value={formData.name}
                  onChange={handleInputChange}
                />
                {validationErrors.name && (
                  <div className="invalid-feedback">{validationErrors.name}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className={`form-control ${validationErrors.email ? "is-invalid" : ""}`}
                  value={formData.email}
                  onChange={handleInputChange}
                />
                {validationErrors.email && (
                  <div className="invalid-feedback">{validationErrors.email}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Mobile Number</label>
                <input
                  type="tel"
                  name="mobile"
                  className={`form-control ${validationErrors.mobile ? "is-invalid" : ""}`}
                  value={formData.mobile}
                  onChange={handleInputChange}
                  maxLength="10" // Limit the length to 10 digits
                />
                {validationErrors.mobile && (
                  <div className="invalid-feedback">{validationErrors.mobile}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  name="username"
                  className={`form-control ${validationErrors.username ? "is-invalid" : ""}`}
                  value={formData.username}
                  onChange={handleInputChange}
                />
                {validationErrors.username && (
                  <div className="invalid-feedback">{validationErrors.username}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Role</label>
                <input
                  type="text"
                  className="form-control"
                  value={profile.role_name || ""}
                  readOnly
                />
              </div>

              <div className="d-flex gap-2">
                {!isEditing ? (
                  <button type="button" className="btn btn-primary" onClick={handleEditToggle}>
                    Edit Profile
                  </button>
                ) : (
                  <>
                    <button type="submit" className="btn btn-success" disabled={updating}>
                      {updating ? "Saving..." : "Save Changes"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleEditToggle}
                      disabled={updating}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  profile: state.profile.profile,
  loading: state.profile.loading,
  updating: state.profile.updating,
});

const mapDispatchToProps = {
  getAppProfile,
  updateProfile,
};

export default connect(mapStateToProps, mapDispatchToProps)(AppProfile);
